import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CircleNotch, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.pexels.com/photos/26559582/pexels-photo-26559582.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, checkAuth } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.email.trim() || !form.password) { toast.error("Enter your email and password"); return; }
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      await api.post(path, { email: form.email.trim(), password: form.password, name: form.name.trim() });
      await checkAuth();
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex bg-[#FCFAF8]">
      {/* Left brand */}
      <div className="hidden lg:block relative w-1/2 z-glow">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF4F0]/80 to-[#FCFAF8]/60" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <button onClick={() => navigate("/")} data-testid="back-home-btn" className="inline-flex items-center gap-2 text-sm text-[#594F4D] hover:text-[#2A2625] transition-colors">
            <ArrowLeft size={16} /> Back home
          </button>
          <div className="flex items-center gap-2 mb-2">
            <img src="/clover-logo.png" alt="Zeyrodha.AI" className="w-9 h-9 object-contain" />
            <span className="font-display text-xl font-semibold tracking-tight">Zeyrodha<span className="text-[#2F9E5A]">.AI</span></span>
          </div>
          <div>
            <h2 className="font-display text-4xl font-semibold tracking-tight leading-tight max-w-md">
              Build apps, media & financials — orchestrated by AI.
            </h2>
            <p className="mt-4 text-[#594F4D] max-w-sm">App Builder, AI Workspace, Media Studio, Accounting, TaxOS & GST — one premium platform.</p>
          </div>
        </div>
      </div>

      {/* Right auth form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <img src="/clover-logo.png" alt="Zeyrodha.AI" className="w-8 h-8 object-contain" />
            <span className="font-display text-lg font-semibold">Zeyrodha<span className="text-[#2F9E5A]">.AI</span></span>
          </div>
          <h1 className="font-display text-2xl font-semibold text-[#2A2625]">{mode === "login" ? "Sign in" : "Create your account"}</h1>
          <p className="text-sm text-[#8C8381] mt-1">{mode === "login" ? "Welcome back to Zeyrodha.AI." : "Start building in minutes."}</p>

          <form onSubmit={submit} className="mt-6 space-y-3">
            {mode === "register" && (
              <input data-testid="login-name-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-white border border-[#2A2625]/15 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
            )}
            <input data-testid="login-email-input" type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-white border border-[#2A2625]/15 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
            <input data-testid="login-password-input" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-white border border-[#2A2625]/15 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
            <button data-testid="login-submit-button" type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-[#2F9E5A] hover:bg-[#278a4e] text-white font-medium rounded-full py-3 transition-colors disabled:opacity-60">
              {busy ? <CircleNotch size={18} className="animate-spin" /> : <ArrowRight size={18} weight="bold" />} {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="text-sm text-[#8C8381] mt-5 text-center">
            {mode === "login" ? "New to Zeyrodha.AI? " : "Already have an account? "}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#2F9E5A] font-medium hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
